// What the trip costs, from the zones it actually travels through.
//
// Myki is priced on the highest zone TOUCHED, not on the two ends. A Zone 1
// journey that runs out through Zone 2 and back — which is most cross-city
// trips on the Frankston and Werribee groups — is a Zone 1–2 fare, and a screen
// that read only the endpoints would quote somebody a cheaper ticket than the
// one they need. So the span is a union over every stop of every train leg.
//
// PURE, and over the BUNDLE. The zones ship inside `src/data/network.json`
// (written by `scripts/fetch-network.mjs`), so working out which zones a
// journey spans costs no request at all — only the two-or-three prices
// themselves come from the server, and there are about three live pairs on the
// whole network.

import { stationById } from './network.js';

/**
 * Every stop a leg puts you on a train through, as stop ids.
 *
 * `stops` is the run's OWN pattern for the leg, which is the truth — it knows
 * about expresses, and the bundled corridor does not. `via` is the planner's
 * shape before a pattern has been read, and the two ends are the floor: a leg
 * that carries neither still spans the zones of the stations it joins.
 */
function stopIdsOf(leg) {
  const ids = [Number(leg?.fromStopId), Number(leg?.toStopId)];
  const rows = Array.isArray(leg?.stops) && leg.stops.length
    ? leg.stops
    : (Array.isArray(leg?.via) ? leg.via : []);
  for (const row of rows) ids.push(Number(row?.stopId));
  return ids.filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * How far out myki prices a journey: zone 2, and no further.
 *
 * The bundle carries `zones` for regional stations too, and they go up to
 * seventeen — but those are V/LINE's zones, the ones a regional fare is
 * calculated in, and they are not myki zones. Myki is zones 1 and 2. The
 * boundary is visible in the bundle itself: every station a metropolitan line
 * serves is zone 1 or 2, and the first station past the metropolitan network
 * carries a zone above it — Lara is `[2, 3, 4]` and is not a myki station,
 * while Wyndham Vale, one stop closer, is `[2]` and is.
 *
 * So "is this a myki trip" is answerable off the bundle with no table of
 * exceptions to maintain, and it answers the awkward ones correctly: Seymour
 * `[6]`, Waurn Ponds `[4]` and Ballarat `[8]` are all V/Line fares.
 */
export const MYKI_MAX_ZONE = 2;

/**
 * The zones this journey is priced across, or null.
 *
 * WALKS ARE NOT COUNTED. The only walks this planner proposes are between two
 * halves of one city station, and you are not ticketed for crossing a subway.
 *
 * A BOUNDARY STATION COUNTS BOTH WAYS. PTV publishes `[1, 2]` for the stations
 * that sit on the line between zones, and both go into the union — which can
 * only ever widen the span, never narrow it. That is the right direction to be
 * wrong in: the app quotes the fare that certainly covers the trip.
 *
 * NULL IS NOT ONLY "NO ZONES AT ALL" ANY MORE. Since the planner will route
 * onto V/Line, three answers here are all "we cannot price this", and the
 * caller draws nothing for each of them — never a dash, never a guess, because
 * a price is the one thing on this screen somebody hands over money because of:
 *
 *   * no stop on the journey has a zone (Wangaratta, Warrnambool — PTV
 *     publishes no `ticket_zones` for them at all);
 *   * a stop the bundle KNOWS has no zone, which is the same fact seen on a
 *     journey whose other half is in town: Southern Cross to Wangaratta is not
 *     a Zone 1 trip;
 *   * any zone past `MYKI_MAX_ZONE`, which is a V/Line fare bought separately.
 *
 * A stop the bundle does not know at all is passed over rather than treated as
 * evidence: it is a gap in our data, not a statement about anybody's ticket.
 */
export function zoneSpan(option) {
  const legs = Array.isArray(option?.legs) ? option.legs : [];
  const zones = new Set();

  for (const leg of legs) {
    // A WALK IS NOT THE ONLY THING THAT IS NOT A TRAIN ANY MORE. The reroute
    // engine builds `kind: 'bus'` legs for a replacement service and a
    // distance-based `kind: 'walk'` for the last kilometre home, and neither
    // is priced by a myki zone. A train leg carries no `kind` at all, or the
    // word 'train'; anything else is passed over.
    if (leg?.kind && leg.kind !== 'train') continue;
    for (const stopId of stopIdsOf(leg)) {
      const station = stationById(stopId);
      if (!station) continue;
      const own = (station.zones ?? []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
      if (!own.length) return null;
      for (const zone of own) zones.add(zone);
    }
  }

  if (!zones.size) return null;
  const all = [...zones];
  const max = Math.max(...all);
  if (max > MYKI_MAX_ZONE) return null;
  return { min: Math.min(...all), max };
}

/** "$2.85", and nothing at all for a price we do not have. */
function money(amount) {
  const n = Number(amount);
  return Number.isFinite(n) && n >= 0 ? `$${n.toFixed(2)}` : '';
}

/** "Zone 1" / "Zones 1–2". An en dash, because it is a range and not a minus. */
function zoneWords(span) {
  const { min, max } = span;
  return min === max ? `Zone ${min}` : `Zones ${min}–${max}`;
}

/**
 * "$2.85 · Zone 1" — the two-hour peak fare and what it buys.
 *
 * PEAK ONLY, deliberately. Off-peak is cheaper, and quoting it would mean the
 * app understates the cost of the 8:10 to somebody standing in front of a
 * ticket machine. The footnote on the screen is where "cheaper off-peak, and
 * capped daily" is said; the number itself is the one that is never too low.
 *
 * An empty string wherever the answer is not known — no fare, no zones, a
 * passenger type the payload did not carry. The caller omits the line entirely
 * rather than drawing "$—".
 */
export function fareLabel(fare, span, { concession = false } = {}) {
  if (!fare || !span) return '';
  const min = Number(span.min);
  const max = Number(span.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return '';

  const price = money((concession ? fare.concession : fare.full)?.twoHour);
  if (!price) return '';
  return `${price} · ${zoneWords({ min, max })}`;
}
